import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Chatbot from "@/components/Chatbot";
import MobileDonateFab from "@/components/MobileDonateFab";
import ContextualGivePrompt from "@/components/ContextualGivePrompt";
import ScrollProgress from "@/components/ScrollProgress";
import AuthCallback from "@/components/AuthCallback";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import ErrorBoundary from "@/components/ErrorBoundary";
import { I18nProvider } from "@/lib/i18n";

// Eager-load homepage for fastest LCP
import Home from "@/pages/Home";

// Lazy-load all other routes for smaller initial bundle
const About = lazy(() => import("@/pages/About"));
const Gallery = lazy(() => import("@/pages/Gallery").then(m => ({ default: m.GalleryIndex })));
const GalleryDetail = lazy(() => import("@/pages/Gallery").then(m => ({ default: m.GalleryDetail })));
const EyesOfHope = lazy(() => import("@/pages/EyesOfHope"));
const Adanobi = lazy(() => import("@/pages/Adanobi"));
const JournalIndex = lazy(() => import("@/pages/Journal").then(m => ({ default: m.JournalIndex })));
const JournalDetail = lazy(() => import("@/pages/Journal").then(m => ({ default: m.JournalDetail })));
const Store = lazy(() => import("@/pages/Store"));
const Voices = lazy(() => import("@/pages/Voices"));
const Philosophy = lazy(() => import("@/pages/Philosophy"));
const Contact = lazy(() => import("@/pages/Contact"));
const FoundersCircle = lazy(() => import("@/pages/FoundersCircle"));
const PaymentSuccess = lazy(() => import("@/pages/Payment").then(m => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/Payment").then(m => ({ default: m.PaymentCancel })));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Privacy = lazy(() => import("@/pages/Legal").then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import("@/pages/Legal").then(m => ({ default: m.Terms })));
const Transparency = lazy(() => import("@/pages/Transparency"));
const Heritage = lazy(() => import("@/pages/Heritage"));
const CustomPage = lazy(() => import("@/pages/CustomPage"));
const Press = lazy(() => import("@/pages/Press"));
const Volunteer = lazy(() => import("@/pages/Volunteer"));
const Museum = lazy(() => import("@/pages/Museum"));
const HumanityMap = lazy(() => import("@/pages/HumanityMap"));
const OralHistory = lazy(() => import("@/pages/OralHistory"));
const Films = lazy(() => import("@/pages/Films"));
const Legacy = lazy(() => import("@/pages/Legacy"));
const LegacyDetail = lazy(() => import("@/pages/LegacyDetail"));
const Account = lazy(() => import("@/pages/Account"));
const EchoAlliance = lazy(() => import("@/pages/EchoAlliance"));
const Journeys = lazy(() => import("@/pages/Journeys"));
const GhanaTravel = lazy(() => import("@/pages/GhanaTravel"));
const TravelPlanner = lazy(() => import("@/pages/TravelPlanner"));
const TravelMap = lazy(() => import("@/pages/TravelMap"));
const TravelExplore = lazy(() => import("@/pages/TravelExplore"));

function RouteFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="route-loading"
      className="min-h-screen flex items-center justify-center bg-[#0a0908]"
    >
      <div className="w-10 h-10 border-2 border-[#d4af37]/30 border-t-[#d4af37] rounded-full animate-spin" />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 200);
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function Shell({ children }) {
  const { pathname, hash } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      {!isAdmin && <Navigation />}
      <div id="main-content">{children}</div>
      {!isAdmin && <Footer />}
      {!isAdmin && <Chatbot />}
      {!isAdmin && <MobileDonateFab />}
      {!isAdmin && <ScrollProgress />}
      {!isAdmin && <ContextualGivePrompt />}
      <AnalyticsTracker />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <I18nProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            <Shell>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/gallery/:slug" element={<GalleryDetail />} />
                  <Route path="/eyes-of-hope" element={<EyesOfHope />} />
                  <Route path="/adanobi" element={<Adanobi />} />
                  <Route path="/journal" element={<JournalIndex />} />
                  <Route path="/journal/:slug" element={<JournalDetail />} />
                  <Route path="/store" element={<Store />} />
                  <Route path="/voices" element={<Voices />} />
                  <Route path="/philosophy" element={<Philosophy />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/founders-circle" element={<FoundersCircle />} />
                  <Route path="/heritage" element={<Heritage />} />
                  <Route path="/press" element={<Press />} />
                  <Route path="/volunteer" element={<Volunteer />} />
                  <Route path="/museum" element={<Museum />} />
                  <Route path="/map" element={<HumanityMap />} />
                  <Route path="/oral-history" element={<OralHistory />} />
                  <Route path="/films" element={<Films />} />
                  <Route path="/legacy" element={<Legacy />} />
                  <Route path="/legacy/:slug" element={<LegacyDetail />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/echo-alliance" element={<EchoAlliance />} />
                  <Route path="/journeys" element={<Journeys />} />
                  <Route path="/travel" element={<GhanaTravel />} />
                  <Route path="/travel/planner" element={<TravelPlanner />} />
                  <Route path="/travel/map" element={<TravelMap />} />
                  <Route path="/travel/explore" element={<TravelExplore />} />
                  <Route path="/p/:slug" element={<CustomPage />} />
                  <Route path="/transparency" element={<Transparency />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/cancel" element={<PaymentCancel />} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                </Routes>
              </Suspense>
            </Shell>
            <Toaster
              theme="dark"
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#141210",
                  border: "1px solid rgba(212,175,55,0.4)",
                  color: "#f4f0ec",
                  borderRadius: "0",
                  fontFamily: "Outfit, sans-serif",
                },
              }}
            />
          </BrowserRouter>
        </div>
      </I18nProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
